import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { toast } from "./ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Phone, MessageSquare, Mail, Loader2, Send } from "lucide-react";
import { useSmsDelivery, SmsDeliveryBadge } from "./SmsDelivery";
import { SmsConsentHint } from "./SmsConsentHint";

import { T } from "../lib/i18n";

// Unified "contact" actions: Call (Twilio bridge), SMS (Twilio), Email (SendGrid).
// variant: "inline" (default) | "map" (compact pills) | "icons" (icon-only buttons, e.g. RFQ invite rows)
// presetSms / presetSubject / presetEmail: 打开弹窗时预填内容
// highlight（可选，SYS-006 ③）：列表搜索时把命中的号码片段标出来。为什么放在这里而不是外层：
//   电话号码是按钮内的文本（点击直接拨号），外层无法只替换文本节点而不破坏按钮结构；
//   不传该参数时行为与原来完全一致，其他调用点（地图、RFQ、联系人编辑器）不受影响。
export default function ContactActions({ phone, email, name = "", variant = "inline", idBase = "contact", presetSms = "", presetSubject = "", presetEmail = "", panelMode = false, highlight }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [smsOpen, setSmsOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [smsMsg, setSmsMsg] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const delivery = useSmsDelivery(api);

  if (!phone && !email) return null;

  const openSms = () => { setSmsMsg((v) => v || presetSms); setSmsOpen(true); };
  const openMail = () => { setSubject((v) => v || presetSubject); setEmailBody((v) => v || presetEmail); setMailOpen(true); };

  // Dial through the in-browser Softphone (WebRTC) instead of the bridge callback.
  const doCall = () => {
    if (!phone) return;
    window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number: phone, name } }));
    toast.success(zh ? "正在用网页拨号呼叫…（右下角软电话）" : "Dialing via softphone…");
  };

  const sendSms = async () => {
    if (!smsMsg.trim()) return;
    setSending(true);
    try {
      delivery.clear();
      const { data } = await api.post("/telephony/sms", { to: phone, message: smsMsg.trim() });
      if (data.ok) { toast.success(zh ? "短信已提交，等待运营商投递回执…" : "SMS submitted"); setSmsMsg(""); delivery.track(data.info); }
      else toast.error(data.hint || data.info || (zh ? "发送失败" : "Send failed"), { duration: 7000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Send failed"));
    }
    setSending(false);
  };

  const sendEmail = async () => {
    if (!emailBody.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/telephony/email", { to: email, subject: subject.trim(), body: emailBody.trim() });
      if (data.ok) { toast.success(zh ? "邮件已发送" : "Email sent"); setMailOpen(false); setSubject(""); setEmailBody(""); }
      else toast.error(data.hint || data.info || (zh ? "发送失败" : "Send failed"), { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Send failed"));
    }
    setSending(false);
  };

  const isMap = variant === "map";
  const isIcons = variant === "icons";
  // 工程联络浮层位于 z-60：其通信弹窗需盖过面板，手机操作同时保留 44px 触控区。
  // 仅由联络面板启用，不改变其它页面的既有布局和发送流程。
  const touchCls = panelMode ? " min-h-11 min-w-11 px-3 sm:min-h-9" : "";
  const callCls = isIcons
    ? "inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-600 hover:text-white transition-colors"
    : isMap
    ? "inline-flex items-center gap-1 text-[11px] text-white bg-green-600 hover:bg-green-700 rounded-md px-2 py-1 disabled:opacity-60"
    : "inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 disabled:opacity-60";
  const smsCls = isIcons
    ? "inline-flex items-center justify-center h-6 w-6 rounded-full bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-600 hover:text-white transition-colors"
    : isMap
    ? "inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded-md px-2 py-1"
    : "inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900";
  const mailCls = isIcons
    ? "inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-600 hover:text-white transition-colors"
    : isMap
    ? "inline-flex items-center gap-1 text-[11px] text-blue-700 border border-blue-300 hover:bg-blue-50 rounded-md px-2 py-1"
    : "inline-flex items-center gap-1 text-blue-700 hover:text-blue-900";

  return (
    <>
      <div className={panelMode ? "flex flex-wrap items-center gap-2" : isIcons ? "inline-flex items-center gap-1" : isMap ? "flex flex-wrap items-center gap-1" : "flex flex-wrap items-center gap-x-3 gap-y-1"}>
        {phone && (
          <button type="button" data-testid={`${idBase}-call`} onClick={doCall} title={isIcons ? `${zh ? "拨打" : "Call"} ${phone}` : phone} className={callCls + touchCls}>
            <Phone className="h-3 w-3" />
            {isIcons ? null : isMap ? (zh ? "拨打" : "Call") : (highlight ? highlight(phone) : phone)}
          </button>
        )}
        {phone && (
          <button type="button" data-testid={`${idBase}-sms`} onClick={openSms} title={isIcons ? `${zh ? "发短信" : "SMS"} ${phone}` : undefined} className={smsCls + touchCls}>
            <MessageSquare className="h-3 w-3" />{isIcons ? null : (zh ? "短信" : "SMS")}
          </button>
        )}
        {email && (
          <button type="button" data-testid={`${idBase}-email`} onClick={openMail} title={isIcons ? `${zh ? "发邮件" : "Email"} ${email}` : email} className={mailCls + touchCls}>
            <Mail className="h-3 w-3" />{isIcons ? null : isMap ? (zh ? "邮件" : "Email") : email}
          </button>
        )}
      </div>

      <Dialog open={smsOpen} onOpenChange={(o) => !o && setSmsOpen(false)}>
        <DialogContent className={`max-w-md ${panelMode ? "z-[70]" : ""}`} overlayClassName={panelMode ? "z-[69]" : undefined} data-testid={`${idBase}-sms-dialog`}>
          <DialogHeader><DialogTitle>{zh ? "发送短信" : "Send SMS"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{zh ? "发送至" : "To"}: <span className="font-medium text-slate-700">{name ? `${name} · ` : ""}{phone}</span></p>
            {smsOpen && <SmsConsentHint api={api} zh={zh} phone={phone} testid={`${idBase}-sms-consent-hint`} />}
            <SmsDeliveryBadge st={delivery.st} zh={zh} />
            <Textarea data-testid={`${idBase}-sms-input`} value={smsMsg} onChange={(e) => setSmsMsg(e.target.value)} rows={4} maxLength={1500}
              placeholder={zh ? "输入短信内容…" : "Type your message…"} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setSmsOpen(false); delivery.clear(); }}>{delivery.st ? (zh ? "关闭" : "Close") : (zh ? "取消" : "Cancel")}</Button>
              <Button type="button" data-testid={`${idBase}-sms-send`} onClick={sendSms} disabled={sending || !smsMsg.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}{zh ? "发送" : "Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mailOpen} onOpenChange={(o) => !o && setMailOpen(false)}>
        <DialogContent className={`max-w-lg ${panelMode ? "z-[70]" : ""}`} overlayClassName={panelMode ? "z-[69]" : undefined} data-testid={`${idBase}-email-dialog`}>
          <DialogHeader><DialogTitle>{zh ? "发送邮件" : "Send Email"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{zh ? "发送至" : "To"}: <span className="font-medium text-slate-700">{name ? `${name} · ` : ""}{email}</span></p>
            {mailOpen && <SmsConsentHint api={api} zh={zh} email={email} testid={`${idBase}-email-consent-hint`} />}
            <Input data-testid={`${idBase}-email-subject`} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
              placeholder={zh ? "邮件主题" : "Subject"} />
            <Textarea data-testid={`${idBase}-email-body`} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6}
              placeholder={zh ? "输入邮件正文…" : "Type your email…"} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setMailOpen(false)}>{zh ? "取消" : "Cancel"}</Button>
              <Button type="button" data-testid={`${idBase}-email-send`} onClick={sendEmail} disabled={sending || !emailBody.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
                {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}{zh ? "发送" : "Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
